import { Autocomplete, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, Modal, ModalDialog, Typography } from "@mui/joy";
import React from "react";
import WrappedButton from "../WrappedButton";
import { Util } from "../Helpers/Util";

interface PackingListItem {
	packing_list_item_id: string;
	line_number: number;
	description: string;
}

interface TicketOption {
	tickets_id: string;
	ticket_number: string;
	serial_number: string;
	date_entered: string;
	ticket_types: string;
}

interface LinkToTicketDialogProps {
	open: boolean;
	packingListId: string;
	buildingName: string;
	estimateNumber: string;
	onClose: () => void;
	onSaved: () => void;
}

/**
 * Dialog for linking unlinked work order line items to a ticket at the same building
 */
export default function LinkToTicketDialog(props: LinkToTicketDialogProps) {
	const [items, setItems] = React.useState<PackingListItem[]>([]);
	const [selectedItemIds, setSelectedItemIds] = React.useState<Set<string>>(new Set());
	const [tickets, setTickets] = React.useState<TicketOption[]>([]);
	const [selectedTicket, setSelectedTicket] = React.useState<TicketOption | null>(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);

	React.useEffect(() => {
		if (!props.open || !props.packingListId) return;

		setIsLoading(true);
		setSelectedTicket(null);
		setSelectedItemIds(new Set());

		const buildingIdPromise = fetch("PackingList/GetBuildingIdForPackingList", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ packingListId: props.packingListId }),
		}).then((x) => x.json());

		const itemsPromise = fetch("PackingList/GetRemainingPackingListItems", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ packingListId: props.packingListId }),
		}).then((x) => x.json());

		Promise.all([buildingIdPromise, itemsPromise])
			.then(([buildingId, remainingItems]: [string, PackingListItem[]]) => {
				setItems(remainingItems);
				setSelectedItemIds(new Set(remainingItems.map((i) => i.packing_list_item_id)));

				return fetch("Ticket/GetTicketsForWorkOrderLink", {
					method: "POST",
					cache: "no-cache",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ buildingId }),
				});
			})
			.then((x) => x?.json())
			.then((x: TicketOption[]) => {
				if (x) setTickets(x);
				setIsLoading(false);
			});
	}, [props.open, props.packingListId]);

	const toggleItem = (itemId: string) => {
		setSelectedItemIds((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) next.delete(itemId);
			else next.add(itemId);
			return next;
		});
	};

	const toggleAll = () => {
		if (selectedItemIds.size === items.length) {
			setSelectedItemIds(new Set());
		} else {
			setSelectedItemIds(new Set(items.map((i) => i.packing_list_item_id)));
		}
	};

	const saveLinks = () => {
		if (!selectedTicket) {
			alert("Please select a ticket.");
			return;
		}
		if (selectedItemIds.size === 0) {
			alert("Please select at least one line item.");
			return;
		}

		setIsSaving(true);
		fetch("PackingList/SaveTicketPackingListItems", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				ticketsId: selectedTicket.tickets_id,
				itemIds: Array.from(selectedItemIds),
			}),
		}).then(() => {
			setIsSaving(false);
			props.onSaved();
		});
	};

	const formatTicketLabel = (ticket: TicketOption) => {
		const date = Util.Formatter(ticket.date_entered, "D");
		return `#${ticket.ticket_number} — ${ticket.serial_number} — ${date} — ${ticket.ticket_types}`;
	};

	return (
		<Modal open={props.open}>
			<ModalDialog sx={{ width: 700, maxHeight: "85vh", overflowY: "auto" }}>
				<DialogTitle>Link to Ticket</DialogTitle>
				<DialogContent>
					<Typography level="title-md" sx={{ mb: 0.5 }}>
						{props.buildingName}
					</Typography>
					<Typography level="body-sm" sx={{ mb: 1.5 }}>
						Estimate #{props.estimateNumber}
					</Typography>

					<Divider sx={{ mb: 1.5 }} />

					<Typography level="title-sm" sx={{ mb: 0.5 }}>
						Unlinked Line Items
					</Typography>
					{isLoading ? (
						<Typography level="body-sm">Loading...</Typography>
					) : items.length === 0 ? (
						<Typography level="body-sm" sx={{ mb: 1.5 }}>
							All line items are already linked to a ticket.
						</Typography>
					) : (
						<>
							<Checkbox
								label="Select all"
								checked={selectedItemIds.size === items.length}
								indeterminate={selectedItemIds.size > 0 && selectedItemIds.size < items.length}
								onChange={toggleAll}
								sx={{ mb: 1 }}
							/>
							{items.map((item) => (
								<Checkbox
									key={item.packing_list_item_id}
									label={item.description}
									checked={selectedItemIds.has(item.packing_list_item_id)}
									onChange={() => toggleItem(item.packing_list_item_id)}
									sx={{ display: "block", mb: 0.5 }}
								/>
							))}
						</>
					)}

					<Divider sx={{ my: 1.5 }} />

					<Typography level="title-sm" sx={{ mb: 0.5 }}>
						Select Ticket
					</Typography>
					<Typography level="body-xs" sx={{ mb: 1, color: "text.tertiary" }}>
						Showing tickets for this building only
					</Typography>
					<Autocomplete
						options={tickets}
						getOptionLabel={formatTicketLabel}
						isOptionEqualToValue={(opt, val) => opt.tickets_id === val.tickets_id}
						openOnFocus
						placeholder="Select ticket"
						value={selectedTicket}
						onChange={(event, value) => setSelectedTicket(value)}
						disabled={isLoading || items.length === 0}
						sx={{ width: "100%" }}
					/>
				</DialogContent>
				<DialogActions>
					<WrappedButton onClick={saveLinks} busy={isSaving} disabled={isLoading || items.length === 0}>
						Link Items
					</WrappedButton>
					<Button onClick={props.onClose} color="neutral">
						Cancel
					</Button>
				</DialogActions>
			</ModalDialog>
		</Modal>
	);
}
